import {Component, OnInit} from '@angular/core';
import {Address} from '../../../models/response/address/address';
import {ProspectSearchRequest} from '../../../models/request/prospect/prospect-search-request';
import {AuthService} from '../../../services/auth.service';
import {User} from '../../../models/response/user';
import {RestResult} from '../../../models/response/rest-result';
import {ProspectService} from '../../../services/prospect.service';
import {Prospect} from '../../../models/response/prospect/prospect';
import {SmartTableUtils} from '../../../../modules/pages/buyer/common/smart-table-utils';
import {Constant} from '../../../common/constant';
import {LocalDataSource} from 'ng2-smart-table';
import {CustomRow} from '../../../models/common/custom-row';
import {CustomerService} from '../../../services/customer.service';
import {CustomerRequest} from '../../../models/request/prospect/customer-request';
import {CommonService} from '../../../services/common.service';
import {CustomerLinkRequest} from '../../../models/request/prospect/customer-link-request';
import {NbDialogRef} from '@nebular/theme';
import {ProspectResidenceLookupRequest} from '../../../models/request/prospect/prospect-residence-lookup-request';
import {StringUtils} from '../../../common/string-utils';
import {forkJoin} from 'rxjs';
import {finalize} from 'rxjs/operators';

@Component({
  selector: 'ngx-buyer-lookup',
  templateUrl: './buyer-lookup.component.html',
  styleUrls: ['./buyer-lookup.component.scss']
})
export class BuyerLookupComponent implements OnInit {

  address: Address;
  residenceLookupRequest: ProspectResidenceLookupRequest;
  lookupOnly = false;
  lookupTitle: string;
  searchRequest = new ProspectSearchRequest();
  currentUser: User;
  buyerCustomer: CustomerRequest;
  selectedProspects = new Map<number, Prospect>();
  isSubmitting = false;
  source: LocalDataSource = new LocalDataSource();
  pageSize = Constant.PAGE_SIZE;
  currentPage = 1;
  columnsDefinition = {
    name: {
      enable: true,
      value: SmartTableUtils.createColumn('Name', ['customer.name']),
    },
    surname: {
      enable: true,
      value: SmartTableUtils.createColumn('Surname', ['customer.surname']),
    },
    mobilePhone: {
      enable: true,
      value: SmartTableUtils.createColumn('Mobile', ['customer.mobilePhone']),
    },
    address: {
      enable: true,
      value: SmartTableUtils.createColumn('Address', ['lookupAddress']),
    },
  };

  settings = {
    selectedRowIndex: -1,
    mode: 'external',
    selectMode: 'multi',
    hideSubHeader: true,
    actions: {
      add: false,
      edit: false,
      delete: false,
    },
    pager: {
      display: true,
      perPage: this.pageSize,
    },
    columns: SmartTableUtils.createColumns(this.columnsDefinition),
  };

  constructor(private authService: AuthService,
              private prospectService: ProspectService,
              private customerService: CustomerService,
              private commonService: CommonService,
              private ref: NbDialogRef<any>) {
  }

  ngOnInit(): void {
    this.currentUser = this.authService.currentUser;
    this.loadData();
  }

  loadData() {
    if (this.residenceLookupRequest) {
      this.prospectService.searchResidenceLookup(this.residenceLookupRequest).subscribe((result: RestResult) => {
        this.loadLookupResult(result.data);
      });
      return;
    }

    this.searchRequest.officeId = this.currentUser.office?.officeId;
    this.searchRequest.streetId = this.address.streetId;
    this.searchRequest.districtId = this.address.districtId;
    this.searchRequest.cityId = this.address.cityId;
    if (this.address.numberFrom && !this.address.number) {
      this.address.number = this.address.numberFrom;
    }
    this.searchRequest.number = this.address.number;
    this.prospectService.searchByAddress(this.searchRequest).subscribe((result: RestResult) => {
      this.loadLookupResult(result.data);
    });
  }

  loadLookupResult(result: any) {
    if (!result) {
      this.selectedProspects.clear();
      this.source.load([]);
      return;
    }

    const rows = Array.isArray(result) ? result : [result];
    const prospects = rows
      .map(item => item?.prospect ? item.prospect : item)
      .filter(item => !!item);

    this.selectedProspects.clear();
    this.source.load(prospects.map(prospect => {
      const lookupProspect = {
        ...prospect,
        lookupAddress: StringUtils.getFullAddress(prospect.address),
      };
      return new CustomRow(lookupProspect, false, false);
    }));
  }

  updateSelection(event: any) {
    this.selectedProspects.clear();
    for (const row of event?.selected || []) {
      const prospect = (row?.data || row) as Prospect;
      if (prospect?.prospectId) {
        this.selectedProspects.set(prospect.prospectId, prospect);
      }
    }
  }

  copy() {
    const targets = Array.from(this.selectedProspects.values());
    if (!this.buyerCustomer || targets.length === 0 || this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;
    forkJoin(targets.map(target =>
      this.prospectService.createCustomer(target.prospectId, this.buildCopyRequest(target)),
    )).pipe(
      finalize(() => this.isSubmitting = false),
    ).subscribe(() => {
      this.commonService.info(`Copied info to ${targets.length} prospect${targets.length === 1 ? '' : 's'}!`);
      this.ref.close();
    });
  }

  private buildCopyRequest(target: Prospect): CustomerRequest {
    const request = new CustomerRequest();
    request.name = this.buyerCustomer.name;
    request.surname = this.buyerCustomer.surname;
    request.phone = this.buyerCustomer.phone;
    request.mobilePhone = this.buyerCustomer.mobilePhone;
    request.email = this.buyerCustomer.email;
    request.dob = this.buyerCustomer.dob;
    if (target.customer) {
      request.customerId = target.customer.customerId;
    }
    return request;
  }

  trimObject(obj: any, properties: string[]): any {
    const curentProperties: string[] = Object.keys(obj);
    for (const name of curentProperties) {
      if (properties.indexOf(name) === -1) {
        delete obj[name];
      }
    }
  }

  link() {
    const selectedCustomerIds = this.selectedLinkCustomerIds;
    if (!this.buyerCustomer?.customerId || selectedCustomerIds.length === 0 || this.isSubmitting) {
      return;
    }
    const request = new CustomerLinkRequest();
    request.linkedIds = `;${[this.buyerCustomer.customerId, ...selectedCustomerIds].join(';')};`;
    this.isSubmitting = true;
    this.customerService.linkCustomer(request).pipe(
      finalize(() => this.isSubmitting = false),
    ).subscribe(() => {
      this.commonService.info(
        `Linked to ${selectedCustomerIds.length} prospect${selectedCustomerIds.length === 1 ? '' : 's'}!`,
      );
      this.ref.close(true);
    });
  }

  close() {
    this.ref.close();
  }

  get selectedCount(): number {
    return this.selectedProspects.size;
  }

  get canCopy(): boolean {
    return !!this.buyerCustomer && this.selectedCount > 0;
  }

  get canLink(): boolean {
    const buyerCustomerId = this.buyerCustomer?.customerId;
    return !!buyerCustomerId
      && this.selectedCount > 0
      && Array.from(this.selectedProspects.values())
        .every(prospect => !!prospect.customer?.customerId
          && prospect.customer.customerId !== buyerCustomerId);
  }

  private get selectedLinkCustomerIds(): number[] {
    const buyerCustomerId = this.buyerCustomer?.customerId;
    return Array.from(this.selectedProspects.values())
      .map(prospect => prospect.customer?.customerId)
      .filter(customerId => !!customerId && customerId !== buyerCustomerId)
      .filter((customerId, index, values) => values.indexOf(customerId) === index);
  }
}
